import { useState, FormEvent } from 'react';
import { authApi } from '../utils/api';
import { AxiosError } from 'axios';
import { UserRole } from '../types/user';

interface RegisterFormProps {
  onSuccess?: () => void;
}

export default function RegisterForm({ onSuccess }: RegisterFormProps) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.STANDARD);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const generateStrongPassword = () => {
    const length = 16;
    // Exclude similar characters (1, i, l, I, L, o, O, 0) and limit special chars
    const uppercase = "ABCDEFGHJKMNPQRSTUVWXYZ"; // Excluded: I, L, O
    const lowercase = "abcdefghjkmnpqrstuvwxyz"; // Excluded: i, l, o
    const numbers = "23456789"; // Excluded: 0, 1
    const special = "#@$&!?%+="; // Only easy-to-type special chars
    const charset = uppercase + lowercase + numbers + special;
    let password = "";

    // Ensure at least one of each type
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];

    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }

    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');

    setPassword(password);
    setConfirmPassword(password);
    setShowPassword(true);
    setShowConfirmPassword(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate password confirmation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      await authApi.register({
        email,
        username,
        password,
        full_name: fullName || undefined,
        role,
      });
      setSuccess(true);
      setEmail('');
      setUsername('');
      setPassword('');
      setConfirmPassword('');
      setFullName('');
      setRole(UserRole.STANDARD);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string }>;
      setError(axiosError.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          Email
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
          required
          disabled={isLoading}
        />
      </div>

      <div>
        <label htmlFor="reg-username" className="block text-sm font-medium text-gray-700 mb-2">
          Username
        </label>
        <input
          id="reg-username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
          required
          disabled={isLoading}
        />
      </div>

      <div>
        <label htmlFor="full-name" className="block text-sm font-medium text-gray-700 mb-2">
          Full Name (Optional)
        </label>
        <input
          id="full-name"
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
          disabled={isLoading}
        />
      </div>

      <div>
        <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
          Role
        </label>
        <select
          id="role"
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
          disabled={isLoading}
        >
          <option value={UserRole.READONLY}>Read Only</option>
          <option value={UserRole.STANDARD}>Standard User</option>
          <option value={UserRole.MANAGER}>Manager</option>
          <option value={UserRole.ADMIN}>Admin</option>
        </select>
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <label htmlFor="reg-password" className="block text-sm font-medium text-gray-700">
            Password
          </label>
          <button
            type="button"
            onClick={generateStrongPassword}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            disabled={isLoading}
          >
            ⚡ Generate Strong Password
          </button>
        </div>
        <div className="relative">
          <input
            id="reg-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2 pr-10 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
            required
            minLength={8}
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
          >
            {showPassword ? '🙈' : '👁️'}
          </button>
        </div>
      </div>

      <div>
        <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
          Confirm Password
        </label>
        <div className="relative">
          <input
            id="confirm-password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-4 py-2 pr-10 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
            required
            minLength={8}
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
          >
            {showConfirmPassword ? '🙈' : '👁️'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-600">User created successfully!</p>
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full px-6 py-2 btn-primary rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? 'Creating...' : 'Create User'}
      </button>
    </form>
  );
}
